import { useState, useMemo, useEffect } from "react";
import { useAccount, useSendTransaction, useSwitchChain, useBalance, useWriteContract, useEstimateFeesPerGas } from "wagmi";
import { useCryptoPrices } from "@/hooks/useCryptoPrices";
import { AlertTriangle } from "lucide-react";

import { useWallet, useConnection } from "@solana/wallet-adapter-react";
import { useTonAddress, useTonConnectUI, useTonWallet } from "@tonconnect/ui-react";
import { SystemProgram, PublicKey, Transaction, LAMPORTS_PER_SOL } from "@solana/web3.js";
import { parseEther, parseUnits, formatEther } from "viem";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useTranslation } from "react-i18next";
import { useToast } from "@/hooks/use-toast";
import { ArrowUpRight, CheckCircle2, Loader2, ExternalLink } from "lucide-react";
import { NETWORK_TO_CHAIN_ID } from "@/lib/web3-config";
import { ERC20_ABI, getTokensForChain, type TokenInfo } from "@/lib/token-contracts";
import ConnectWalletButton from "@/components/ConnectWalletButton";


interface Web3SendDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const EXPLORER_URLS: Record<string, string> = {
  '1': 'https://etherscan.io/tx/',
  '56': 'https://bscscan.com/tx/',
  '137': 'https://polygonscan.com/tx/',
  '42161': 'https://arbiscan.io/tx/',
  '10': 'https://optimistic.etherscan.io/tx/',
  '8453': 'https://basescan.org/tx/',
  '43114': 'https://snowtrace.io/tx/',
  'solana': 'https://solscan.io/tx/',
  'ton': 'https://tonviewer.com/transaction/',
};

type AssetType = 'native' | string; // 'native' or token symbol like 'USDT'

const Web3SendDialog = ({ open, onOpenChange }: Web3SendDialogProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  
  // EVM
  const { address: evmAddress, chain, isConnected: evmConnected } = useAccount();
  const { switchChain } = useSwitchChain();
  const { sendTransaction: evmSend, isPending: evmPending } = useSendTransaction();
  const { writeContract, isPending: tokenPending } = useWriteContract();
  
  
  // Solana
  const { publicKey: solPublicKey, sendTransaction: solSendTx, connected: solConnected } = useWallet();
  const { connection } = useConnection();
  
  // TON
  const tonAddress = useTonAddress();
  const tonWallet = useTonWallet();
  const [tonConnectUI] = useTonConnectUI();
  const tonConnected = !!tonWallet;
  
  const [recipientAddress, setRecipientAddress] = useState("");
  const [amount, setAmount] = useState("");
  const [selectedNetwork, setSelectedNetwork] = useState("");
  const [selectedAsset, setSelectedAsset] = useState<AssetType>("native");
  const [txHash, setTxHash] = useState<string | null>(null);
  const [sending, setSending] = useState(false);

  const isSolana = selectedNetwork === 'Solana';
  const isTON = selectedNetwork === 'TON';
  const isEVM = !isSolana && !isTON && !!selectedNetwork;
  const targetChainId = isEVM ? NETWORK_TO_CHAIN_ID[selectedNetwork] : undefined;
  const needsSwitch = targetChainId && chain?.id !== targetChainId;
  const isConnected = evmConnected || solConnected || tonConnected;

  const { data: evmBalance } = useBalance({
    address: evmAddress,
    chainId: targetChainId,
    query: { enabled: isEVM && !!evmAddress }
  });

  // Available tokens for the selected EVM chain
  const chainTokens = useMemo(() => {
    if (!targetChainId) return [];
    return getTokensForChain(targetChainId);
  }, [targetChainId]);

  const selectedToken = useMemo(() => {
    if (selectedAsset === 'native' || !isEVM) return null;
    return chainTokens.find(t => t.symbol === selectedAsset) || null;
  }, [selectedAsset, chainTokens, isEVM]);

  // Only Ethereum mainnet is supported
  const availableNetworks: string[] = evmConnected ? ['Ethereum (ERC20)'] : [];

  // Auto-select the only available network
  useEffect(() => {
    if (open && availableNetworks.length === 1 && !selectedNetwork) {
      setSelectedNetwork(availableNetworks[0]);
    }
  }, [open, evmConnected]);

  // Live gas estimate for Ethereum send
  const { data: feeData } = useEstimateFeesPerGas({ chainId: targetChainId, query: { enabled: isEVM } });
  const { cryptoAssets } = useCryptoPrices();
  const ethPrice = cryptoAssets.find(a => a.symbol === 'ETH')?.price || 0;
  const gasEthEstimate = useMemo(() => {
    const maxFee = feeData?.maxFeePerGas ?? feeData?.gasPrice;
    if (!maxFee) return null;
    // Simple ETH transfer ~21,000 gas; ERC20 ~65,000 gas
    const gasUnits = selectedToken ? 65000n : 21000n;
    const wei = maxFee * gasUnits;
    return parseFloat(formatEther(wei));
  }, [feeData, selectedToken]);
  const gasUsdEstimate = gasEthEstimate && ethPrice ? gasEthEstimate * ethPrice : null;


  const handleSend = async () => {
    if (!recipientAddress || !amount || !selectedNetwork) {
      toast({ title: t('common.error'), description: t('common.fillAllFields'), variant: "destructive" });
      return;
    }
    setSending(true);
    try {
      if (isSolana) await handleSolanaSend();
      else if (isTON) await handleTONSend();
      else if (selectedToken) await handleERC20Send();
      else await handleEVMSend();
    } catch (error: any) {
      toast({ title: t('common.error'), description: error.message?.slice(0, 120) || 'Unknown error', variant: "destructive" });
    } finally {
      setSending(false);
    }
  };

  const handleEVMSend = async () => {
    if (!targetChainId) return;
    if (!/^0x[a-fA-F0-9]{40}$/.test(recipientAddress)) {
      toast({ title: t('common.error'), description: t('wallet.addressInvalid'), variant: "destructive" }); return;
    }
    if (needsSwitch) await switchChain({ chainId: targetChainId });
    evmSend(
      { to: recipientAddress as `0x${string}`, value: parseEther(amount), chainId: targetChainId },
      {
        onSuccess: (hash) => { setTxHash(hash); toast({ title: 'تم إرسال المعاملة', description: `TX: ${hash.slice(0, 10)}...` }); },
        onError: (error) => { toast({ title: t('common.error'), description: error.message.slice(0, 100), variant: "destructive" }); },
      }
    );
  };

  const handleERC20Send = async () => {
    if (!targetChainId || !selectedToken) return;
    if (!/^0x[a-fA-F0-9]{40}$/.test(recipientAddress)) {
      toast({ title: t('common.error'), description: t('wallet.addressInvalid'), variant: "destructive" }); return;
    }
    if (needsSwitch) await switchChain({ chainId: targetChainId });
    const parsedAmount = parseUnits(amount, selectedToken.decimals);
    writeContract(
      {
        address: selectedToken.address,
        abi: ERC20_ABI,
        functionName: 'transfer',
        args: [recipientAddress as `0x${string}`, parsedAmount],
        account: evmAddress!,
        chain: chain!,
      },
      {
        onSuccess: (hash) => { setTxHash(hash); toast({ title: `تم إرسال ${selectedToken.symbol}`, description: `TX: ${hash.slice(0, 10)}...` }); },
        onError: (error) => { toast({ title: t('common.error'), description: error.message.slice(0, 100), variant: "destructive" }); },
      }
    );
  };

  const handleSolanaSend = async () => {
    if (!solPublicKey || !solSendTx) { toast({ title: t('common.error'), description: 'يرجى ربط محفظة Solana', variant: "destructive" }); return; }
    let toPubKey: PublicKey;
    try { toPubKey = new PublicKey(recipientAddress); } catch { toast({ title: t('common.error'), description: 'عنوان Solana غير صالح', variant: "destructive" }); return; }
    const lamports = Math.round(parseFloat(amount) * LAMPORTS_PER_SOL);
    const transaction = new Transaction().add(SystemProgram.transfer({ fromPubkey: solPublicKey, toPubkey: toPubKey, lamports }));
    const signature = await solSendTx(transaction, connection);
    setTxHash(signature);
    toast({ title: 'تم إرسال المعاملة', description: `TX: ${signature.slice(0, 10)}...` });
  };

  const handleTONSend = async () => {
    if (!tonConnected) { toast({ title: t('common.error'), description: 'يرجى ربط محفظة TON', variant: "destructive" }); return; }
    const nanotons = Math.round(parseFloat(amount) * 1e9).toString();
    const result = await tonConnectUI.sendTransaction({
      validUntil: Math.floor(Date.now() / 1000) + 600,
      messages: [{ address: recipientAddress, amount: nanotons }],
    });
    setTxHash(result.boc);
    toast({ title: 'تم إرسال المعاملة عبر TON', description: 'تمت الموافقة في المحفظة' });
  };

  const resetForm = () => { setRecipientAddress(""); setAmount(""); setSelectedNetwork(""); setSelectedAsset("native"); setTxHash(null); };

  const getExplorerUrl = () => {
    if (!txHash) return null;
    if (isSolana) return `${EXPLORER_URLS['solana']}${txHash}`;
    if (isTON) return `https://tonviewer.com/`;
    if (targetChainId) return `${EXPLORER_URLS[String(targetChainId)] || ''}${txHash}`;
    return null;
  };

  const explorerUrl = getExplorerUrl();

  if (!isConnected) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>{t('wallet.send') || 'إرسال مباشر على البلوكتشين'}</DialogTitle></DialogHeader>
          <div className="py-6 text-center space-y-4">
            <p className="text-sm text-muted-foreground">
              يجب ربط محفظة Ethereum (مثل MetaMask) لإرسال العملة مباشرة على البلوكتشين دون أي وسيط.
            </p>
            <ConnectWalletButton />
          </div>
        </DialogContent>
      </Dialog>
    );
  }


  const isPending = evmPending || tokenPending || sending;

  const getPlaceholder = () => {
    if (isSolana) return 'عنوان Solana...';
    if (isTON) return 'EQ... أو UQ...';
    return '0x...';
  };

  const getCurrencyLabel = () => {
    if (selectedToken) return selectedToken.symbol;
    if (isSolana) return 'SOL';
    if (isTON) return 'TON';
    return evmBalance?.symbol || 'ETH';
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) resetForm(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowUpRight className="w-5 h-5 text-primary" />
            {t('wallet.sendCrypto') || 'إرسال عملة'}
          </DialogTitle>
          <DialogDescription className="text-xs">{t('wallet.sendFromWallet') || 'إرسال مباشر من محفظتك'}</DialogDescription>
        </DialogHeader>

        {txHash ? (
          <div className="space-y-4 py-4">
            <div className="flex flex-col items-center gap-3">
              <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center">
                <CheckCircle2 className="w-8 h-8 text-green-500" />
              </div>
              <p className="text-sm font-semibold text-foreground">تم إرسال المعاملة!</p>
              <p className="text-xs text-muted-foreground font-mono break-all px-4" dir="ltr">{txHash.length > 80 ? txHash.slice(0, 40) + '...' : txHash}</p>
            </div>
            {explorerUrl && (
              <a href={explorerUrl} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 text-sm text-primary hover:underline">
                <ExternalLink className="w-4 h-4" /> عرض في المستكشف
              </a>
            )}
            <Button onClick={() => { resetForm(); onOpenChange(false); }} className="w-full">إغلاق</Button>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Network info — Ethereum only */}
            <div className="rounded-lg border border-border bg-secondary/40 p-3 flex items-center gap-2">
              <span className="text-lg">⬡</span>
              <div className="flex-1">
                <p className="text-xs text-muted-foreground">الشبكة</p>
                <p className="text-sm font-semibold text-foreground">Ethereum (ERC20)</p>
              </div>
              {needsSwitch && <span className="text-[10px] text-yellow-500">سيتم التبديل تلقائياً ⚠️</span>}
            </div>

            {/* Gas requirement notice */}
            <div className="rounded-lg border border-yellow-500/30 bg-yellow-500/10 p-3 flex gap-2">
              <AlertTriangle className="w-4 h-4 text-yellow-500 shrink-0 mt-0.5" />
              <div className="text-xs space-y-1">
                <p className="font-semibold text-foreground">يجب أن يكون لديك رصيد ETH في محفظتك</p>
                <p className="text-muted-foreground">
                  رسوم الغاز على شبكة Ethereum تُدفع بعملة ETH وتُخصم مباشرة من محفظتك (ليس من المبلغ المُرسل).
                </p>
                {gasEthEstimate !== null ? (
                  <p className="text-foreground">
                    التكلفة التقديرية للإرسال الواحد:{' '}
                    <span className="font-mono font-semibold">~{gasEthEstimate.toFixed(5)} ETH</span>
                    {gasUsdEstimate ? <span className="text-muted-foreground"> (~${gasUsdEstimate.toFixed(2)})</span> : null}
                  </p>
                ) : (
                  <p className="text-muted-foreground">جاري تقدير رسوم الغاز...</p>
                )}
              </div>
            </div>


            {/* Asset Selection (EVM only, when tokens available) */}
            {isEVM && chainTokens.length > 0 && (
              <div className="space-y-2">
                <Label>العملة</Label>
                <Select value={selectedAsset} onValueChange={setSelectedAsset}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="native">
                      <span className="flex items-center gap-2">
                        <span>⬡</span>
                        <span>{evmBalance?.symbol || 'Native'} (أصلي)</span>
                      </span>
                    </SelectItem>
                    {chainTokens.map(token => (
                      <SelectItem key={token.symbol} value={token.symbol}>
                        <span className="flex items-center gap-2">
                          <span>{token.icon}</span>
                          <span>{token.symbol}</span>
                          <span className="text-muted-foreground text-xs">({token.name})</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Recipient */}
            <div className="space-y-2">
              <Label>{t('wallet.walletAddress')}</Label>
              <Input value={recipientAddress} onChange={e => setRecipientAddress(e.target.value)} placeholder={getPlaceholder()} dir="ltr" className="font-mono text-sm" />
            </div>

            {/* Amount */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>{t('wallet.amount')}</Label>
                <span className="text-[10px] text-muted-foreground">{getCurrencyLabel()}</span>
              </div>
              <Input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0.0" dir="ltr" min="0" step="0.0001" />
              {isEVM && !selectedToken && evmBalance && (
                <div className="flex items-center justify-between">
                  <p className="text-xs text-muted-foreground">{t('wallet.available')}: {parseFloat(formatEther(evmBalance.value)).toFixed(6)} {evmBalance.symbol}</p>
                  <button onClick={() => setAmount(formatEther(evmBalance.value))} className="text-xs text-primary hover:underline">{t('wallet.all') || 'الكل'}</button>
                </div>
              )}
            </div>

            <Button onClick={handleSend} disabled={isPending || !recipientAddress || !amount || !selectedNetwork} className="w-full">
              {isPending ? <><Loader2 className="w-4 h-4 animate-spin ml-2" /> جاري الإرسال...</>
              : needsSwitch ? 'تبديل الشبكة وإرسال'
              : `إرسال ${getCurrencyLabel()}`}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default Web3SendDialog;
